import { Component, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MemberInsurerServiceService } from '@app/modules/lps-member-login/services/member-insurer-service.service';
import { Roles } from '@app/modules/lps-member-login/models/commonmodel';
import { Subject, takeUntil } from 'rxjs';
@Component({
  standalone: true,
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule, MatTooltipModule], // Add RouterModule
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent {

  constructor(private MemberInsurerServiceService: MemberInsurerServiceService, private router: Router) { }


  activeIndex = 0;
  @Output() activeIndexChange = new EventEmitter<number>();
  enumRole = Roles;
  @Output() collapsedChange = new EventEmitter<boolean>();
  // Inside your component.ts
  isCollapsed = false;
  private unsubscribe = new Subject<void>();
  userAccess = this.MemberInsurerServiceService.getUserAccess();
  role = this.MemberInsurerServiceService.getLoginUserRole();
  userName = this.MemberInsurerServiceService.getLoginUserName();
  userEmail = this.MemberInsurerServiceService.getLoginUserEmail();
  userGender = this.MemberInsurerServiceService.getLoginUserInfo()?.userDetails?.gender || '';
  currentTime = new Date();
  private timerInterval: any;

  ngOnInit() {
    this.toggleSidebar();
    this.startTimer();

    // Get user gender from login user info
    this.updateUserGender();

    // Listen for logout events by monitoring session storage changes
    this.monitorLogoutEvents();

    this.MemberInsurerServiceService.tabChanged$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(tab => {
        if (tab === 'overview') {
          this.router.navigate(['/dashboard/insurer']);
        }
        else if (tab == 'config') {
          this.router.navigate(['/dashboard/configuration']);
        }
        else {
          if (this.role == this.enumRole.DISTRIBUTOR)
            this.router.navigate(['/dashboard/distributor/applicants']);
          else
            this.router.navigate(['/dashboard/applicants']);
        }
      });
  }

  getMembersMenuItem() {
    if (this.role === this.enumRole.SUPER_ADMIN) {
      return {
        link: '/dashboard/addAdmins',
        label: 'Admins'
      };
    } else if (this.role === this.enumRole.DISTRIBUTOR && this.userAccess != this.enumRole.ADMIN_DISTRIBUTOR) {
      return {
        link: '/dashboard/distributor/applicants',
        label: 'Applicants'
      };
    } else if (
      this.userAccess !== this.enumRole.ADMIN_INSURER &&
      this.userAccess !== this.enumRole.ADMIN_PROVIDER &&
      this.role !== this.enumRole.PROVIDER &&
      this.role !== this.enumRole.SUPER_ADMIN &&
      this.role !== this.enumRole.DISTRIBUTOR
    ) {
      return {
        link: '/dashboard/applicants',
        label: 'Applicants'
      };
    } else {
      return null; // Don’t show anything if no match
    }
  }


  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    this.collapsedChange.emit(this.isCollapsed);
    this.MemberInsurerServiceService.setSidebarState(this.isCollapsed);
  }

  onSidebarMouseEnter() {
    this.isCollapsed = false;
    this.collapsedChange.emit(this.isCollapsed);
    this.MemberInsurerServiceService.setSidebarState(this.isCollapsed);
  }

  onSidebarMouseLeave() {
    this.isCollapsed = true;
    this.collapsedChange.emit(this.isCollapsed);
    this.MemberInsurerServiceService.setSidebarState(this.isCollapsed);
  }

  onNavActive(tab: string): void {
    this.MemberInsurerServiceService.sideBarchangeTab(tab);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.stopTimer();
  }

  setActive(index: number) {
    this.activeIndex = index;
    this.activeIndexChange.emit(index);
  }

  startTimer() {
    // Only start timer if user is logged in
    if (this.MemberInsurerServiceService.isUserLoggedIn() === 'true') {
      this.timerInterval = setInterval(() => {
        this.currentTime = new Date();
      }, 1000); // Update every second
    }
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  getLoginDuration(): string {
    // Check if user is logged in
    if (this.MemberInsurerServiceService.isUserLoggedIn() !== 'true') {
      return '0s';
    }

    const loginStartTime = this.MemberInsurerServiceService.getLoginStartTime();
    if (!loginStartTime) {
      return '0s';
    }

    const diff = this.currentTime.getTime() - loginStartTime.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  }

  monitorLogoutEvents() {
    // Check for logout every second
    setInterval(() => {
      const isLoggedIn = this.MemberInsurerServiceService.isUserLoggedIn();
      if (isLoggedIn === 'false') {
        // User has logged out, stop timer
        this.stopTimer();
      } else if (isLoggedIn === 'true' && !this.timerInterval) {
        // User has logged in, start timer
        this.startTimer();
      }
    }, 1000);
  }

  get dashboardLink(): string {
    const role = this.MemberInsurerServiceService.getLoginUserRole();
    const userAccess = this.MemberInsurerServiceService.getUserAccess();

    if (role === this.enumRole.SUPER_ADMIN) {
      return '/dashboard/superAdmin';
    } 
    else if (role === this.enumRole.INSURER) {
      if (userAccess == this.enumRole.ADMIN_INSURER)
        return '/dashboard/admins';
      else
        return '/dashboard/insurer';
    }
    else if (role === this.enumRole.DISTRIBUTOR) {
      if (userAccess == this.enumRole.ADMIN_DISTRIBUTOR)
        return '/dashboard/admins';
      else
        return '/dashboard/distributor';
    }
    else {
      if (userAccess == this.enumRole.ADMIN_PROVIDER)
        return '/dashboard/admins';
      else
        return '/dashboard/provider';
    }
  }

  /**
   * Update user gender from login user info
   */
  updateUserGender(): void {
    this.userGender = this.MemberInsurerServiceService.getLoginUserInfo()?.userDetails?.gender || '';
  }

  /**
   * Get avatar URL based on user gender
   */
  getAvatarUrl(): string {
    if (this.userGender === 'Male') {
      return '../../../../../../assets/images/avatar.svg';
    } else if (this.userGender === 'Female') {
      return '../../../../../../assets/images/avatar-female.svg';
    } else {
      return '../../../../../../assets/images/avatar.svg'; // Default avatar
    }
  }

  /**
   * Get user icon URL based on user gender
   */
  getUserIconUrl(): string {
    if (this.userGender === 'Male') {
      return '../../../../../../assets/images/avatar.svg';
    } else if (this.userGender === 'Female') {
      return '../../../../../../assets/images/avatar-female.svg';
    } else {
      return '../../../../../../assets/images/avatar.svg'; // Default user icon
    }
  }

}
